import { action } from '@ember/object';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  startOfDay,
  endOfDay,
  getUnixTime,
  addYears,
  subMinutes,
  startOfMonth,
  addMonths,
  subMonths,
  isSameDay,
  addDays,
  subDays,
} from 'date-fns';
import { format } from 'date-fns-tz';
import type WorkplaceDayModel from 'garaje/models/workplace-day';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import { endTimeOptions, getPartialTimes, hourOptions, startTimeOptions } from 'garaje/utils/hour-options';

interface Option {
  value: string;
  label: string;
}

interface MapDateSelectArgs {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  onAllDayToggle: () => void;
  allDayEnabled: boolean;
  selectedTime: { startTime: string; endTime: string };
  onPartialTimeChange?: (key: 'startTime' | 'endTime', time: string) => void;
  workplaceDays: WorkplaceDayModel[];
}

export default class MapDateSelect extends Component<MapDateSelectArgs> {
  @service declare state: StateService;
  @service declare store: StoreService;
  @service declare featureFlags: FeatureFlagsService;

  @tracked showCalendar = false;
  // null means "use the location's current date", set explicitly when the user navigates months
  @tracked _showingDate: Date | null = null;

  @action
  handleFocusOut(event: FocusEvent): void {
    const root = event.currentTarget as HTMLElement;
    const next = (event.relatedTarget as Node) || null;

    if (!next || !root.contains(next)) {
      this.showCalendar = false;
    }
  }

  @action
  goBackOneMonth(): void {
    this._showingDate = subMonths(this.effectiveShowingDate, 1);
  }

  @action
  goToNextMonth(): void {
    this._showingDate = addMonths(this.effectiveShowingDate, 1);
  }

  @action
  onDateSelect(date: Date): void {
    const { onDateSelect } = this.args;
    onDateSelect(date);
  }

  @action
  onPartialTimeChange(key: 'startTime' | 'endTime', option: Option): void {
    const { onPartialTimeChange } = this.args;
    if (onPartialTimeChange) {
      onPartialTimeChange(key, option.value);
    }
  }

  // selectedDate shifted into a browser-local-readable form so plain date-fns
  // calls (format, isSameDay, etc.) report the location's calendar day. Do NOT
  // pass this to components that already do their own timezone projection —
  // <CalendarGrid> uses formatInTimeZone with @timezone, so it takes the raw
  // @selectedDate directly. Double-shifting causes a one-day-off bug.
  get displayDate(): Date {
    const { selectedDate } = this.args;
    return subMinutes(selectedDate, this.state.minutesBetweenTimezones(selectedDate));
  }

  // if the user hasn't manually navigated months yet, open on the location's current date
  get effectiveShowingDate(): Date {
    return this._showingDate ?? this.displayDate;
  }

  get formattedDate(): string {
    const now = new Date();
    const mbt = this.state.minutesBetweenTimezones(now);
    const locationNow = subMinutes(now, mbt);

    const displayDate = this.displayDate;
    if (isSameDay(displayDate, locationNow)) return 'Today';
    if (isSameDay(displayDate, subDays(locationNow, 1))) return 'Yesterday';
    if (isSameDay(displayDate, addDays(locationNow, 1))) return 'Tomorrow';
    return format(displayDate, 'MM/dd/yyyy');
  }

  get currentMonth(): string {
    return format(startOfMonth(this.effectiveShowingDate), 'yyyy-MM-dd');
  }

  get dateLimits(): { startDate: number; endDate: number } {
    // use the same pattern as the controller's selectedDate: shift new Date() by
    // minutesBetweenTimezones before taking startOfDay so we get the location's
    // midnight, not the browser's
    const locationDate = this.state.getOfficeLocationTime(
      startOfDay(subMinutes(new Date(), this.state.minutesBetweenTimezones(new Date()))),
    );

    return {
      startDate: getUnixTime(locationDate),
      endDate: getUnixTime(addYears(locationDate, 2)),
    };
  }

  // Business-hours helpers for the selected date
  get selectedDayOfWeek(): string {
    const { selectedDate } = this.args;
    return format(subMinutes(selectedDate, this.state.minutesBetweenTimezones(selectedDate)), 'EEEE');
  }

  get activeWorkplaceDay(): WorkplaceDayModel | undefined {
    return this.args.workplaceDays?.find((d) => d.active && d.dayOfWeek === this.selectedDayOfWeek);
  }

  get workplaceStartTime(): string {
    return this.activeWorkplaceDay?.startTime ?? '00:00:00';
  }

  get workplaceEndTime(): string {
    return this.activeWorkplaceDay?.endTime ?? '23:59:00';
  }

  // When the location clock is past the configured working-hours end time, the
  // controller extends the time window to end-of-day ('23:59:00'). Use the later
  // of the two values so that the start/end dropdowns stay non-empty.
  get effectiveEndTime(): string {
    const { endTime } = this.args.selectedTime;
    return this.workplaceEndTime.localeCompare(endTime) >= 0 ? this.workplaceEndTime : endTime;
  }

  get computedStartTime(): Date {
    const { selectedDate, allDayEnabled } = this.args;
    const locationDate = this.state.getOfficeLocationTime(startOfDay(selectedDate));

    if (allDayEnabled) {
      return locationDate;
    }

    const [partialStart] = getPartialTimes(locationDate, this.args.selectedTime);
    return partialStart!;
  }

  get computedEndTime(): Date {
    const { selectedDate, allDayEnabled } = this.args;
    const locationDate = this.state.getOfficeLocationTime(startOfDay(selectedDate));

    if (allDayEnabled) {
      return this.state.getOfficeLocationTime(endOfDay(selectedDate));
    }

    const [, partialEnd] = getPartialTimes(locationDate, this.args.selectedTime);
    return partialEnd!;
  }

  get computedStartTimeUnix(): number {
    return getUnixTime(this.computedStartTime);
  }

  get computedEndTimeUnix(): number {
    return getUnixTime(this.computedEndTime);
  }

  get startTimePlaceholder(): string | null {
    return null;
  }

  get endTimePlaceholder(): string | null {
    return this.workplaceEndTimeLabel;
  }

  get partialDayError(): string {
    if (this.featureFlags.isEnabled('shared-desks-scheduling')) {
      const { startTime, endTime } = this.args.selectedTime;
      if (startTime.localeCompare(endTime) >= 0) {
        return 'End time must be after start time';
      }
    }
    return '';
  }

  get locationTimezone(): string {
    return this.state.currentLocation?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  get selectedForToday(): boolean {
    const locationNow = subMinutes(new Date(), this.state.minutesBetweenTimezones(new Date()));
    return isSameDay(this.displayDate, locationNow);
  }

  get nextAvailableStartTime(): string {
    if (this.selectedForToday) {
      const locationNow = subMinutes(new Date(), this.state.minutesBetweenTimezones(new Date()));
      const currentTime = format(locationNow, 'HH:mm:ss');
      const closestInterval = hourOptions(15, true).find(({ value }) => value.localeCompare(currentTime) >= 0);
      const next = closestInterval?.value || '23:45:00';
      // Respect workplace start time as a lower bound
      return next.localeCompare(this.workplaceStartTime) >= 0 ? next : this.workplaceStartTime;
    }
    return this.workplaceStartTime;
  }

  get startHourOptions(): Option[] {
    const { selectedTime } = this.args;
    const options = startTimeOptions(selectedTime.endTime, 15, true);

    // Only allow times >= nextAvailableStartTime and strictly before effectiveEndTime.
    // effectiveEndTime extends to end-of-day when the location clock is past working hours.
    return options.filter((option) => {
      return (
        option.value.localeCompare(this.nextAvailableStartTime) >= 0 &&
        option.value.localeCompare(this.effectiveEndTime) < 0
      );
    });
  }

  get workplaceEndTimeLabel(): string {
    const [hh = 0, mm = 0, ss = 0] = (this.workplaceEndTime || '23:59:00').split(':').map((n) => parseInt(n, 10));
    const d = new Date();
    d.setHours(hh, mm, ss, 0);
    return format(d, 'h:mm aaa');
  }

  get endHourOptions(): Option[] {
    const { selectedTime } = this.args;
    const options = endTimeOptions(selectedTime.startTime, 15, true);

    // Only allow times <= effectiveEndTime and > workplaceStartTime.
    // effectiveEndTime extends to end-of-day when the location clock is past working hours.
    return options.filter((option) => {
      return (
        option.value.localeCompare(this.workplaceStartTime) > 0 &&
        option.value.localeCompare(this.effectiveEndTime) <= 0
      );
    });
  }
}
